/**
 * The intent registry — every question type the local engine answers.
 *
 * `../router.ts` scores every entry here; order in this array does not
 * matter (each intent carries its own `priority` tiebreak).
 *
 * ## Adding an intent (e.g. the operations family)
 *
 * 1. Write it in a family file — `./operations.ts` for root-cause routing,
 *    operator training, the driver tree, SPC and TARP intents — as an
 *    {@link IntentDef}: `id`, `family: 'operations'`, `priority`, two or
 *    three `examples`, optional `needs`, a `score` (usually `cues({...})`)
 *    and an `answer` built with `ctx.kit` (`kit.run(TOOLS.x, …)` for a tool,
 *    `kit.ran('name')` for an inline query, `kit.done(markdown, sources)`).
 * 2. Spread it into {@link INTENTS} below (`...OPERATIONS_INTENTS`).
 * 3. Add its prompts to `CASES` in `../intent-cases.ts`, and any new chip to
 *    the seeds — `npm run check:assistant` fails on a harvested prompt with
 *    no case, and on a case that routes elsewhere.
 * 4. If it should get a persona lead line, add its id to `LENS_INTENTS` in
 *    `../persona-lens.ts`.
 */

import * as cost from './cost.ts';
import * as energyFamily from './energy.ts';
import * as fleet from './fleet.ts';
import * as knowledge from './knowledge.ts';
import * as machine from './machine.ts';
import * as operations from './operations.ts';
import * as parts from './parts.ts';
import * as platform from './platform.ts';
import * as reports from './reports.ts';
import * as service from './service.ts';
import * as work from './work.ts';
import type { IntentDef } from './types.ts';

export type { IntentDef, IntentFamily } from './types.ts';

export const INTENTS: readonly IntentDef[] = [
  // fleet
  fleet.fleetOverview,
  fleet.hotspots,
  fleet.bridgingUnits,
  fleet.riskRanking,
  fleet.predictionConfidence,
  fleet.breakdownTrend,
  fleet.componentFailureHistory,
  // machine
  machine.machineHistory,
  machine.machineLookup,
  machine.whyFailed,
  machine.likelyCauses,
  machine.telemetry,
  machine.changeNext,
  // cost
  cost.costOfInaction,
  cost.incidentCost,
  cost.costLedger,
  cost.jobCost,
  cost.freightDecision,
  // parts
  parts.stockCheck,
  parts.predictedUsageIntent,
  parts.partNetwork,
  parts.partsHoldup,
  // work
  work.jobsWaiting,
  work.approvalsWaiting,
  work.mostExpensiveJob,
  work.agentDraftedWos,
  work.pmDue,
  work.moduleWaiting,
  // service
  service.dispatch,
  service.training,
  service.customerSatisfaction,
  service.remoteSupport,
  service.kbSessions,
  service.contracts,
  // knowledge
  knowledge.docLookup,
  knowledge.findMentions,
  knowledge.procedureFor,
  // platform
  platform.asset360,
  platform.moduleAgents,
  platform.agentAutonomy,
  platform.fleetRules,
  platform.liveData,
  // reports
  reports.reportPick,
  reports.reportBuild,
  reports.reportFocus,
  // energy
  energyFamily.energy,
  // operations — Paul Telford's feedback: routing, coaching, driver tree, SPC, TARP, FMEA
  operations.routeObservation,
  operations.operatorTraining,
  operations.shiftDriver,
  operations.targetFirst,
  operations.spcStatus,
  operations.tarpStatus,
  operations.fmeaTop,
  operations.controlPlan,
];

export const INTENT_BY_ID: Readonly<Record<string, IntentDef>> = Object.fromEntries(
  INTENTS.map((i) => [i.id, i]),
);
